import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Check, X, Clock } from 'lucide-react';
import { useApp } from '../context/AppContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PaymentSuccessPage = () => {
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const { clearCart } = useApp();
  const hasChecked = useRef(false);

  useEffect(() => {
    if (hasChecked.current) return;
    hasChecked.current = true;
    checkPaymentStatus();
  }, []);

  const checkPaymentStatus = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setPaymentData(response.data);
          setStatus('success');
          clearCart();
          return;
        } else if (response.data.status === 'expired') {
          setStatus('error');
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setStatus('pending');
        }
      } catch (e) {
        console.error(e);
        setStatus('error');
      }
    };

    poll();
  };

  return (
    <div className="min-h-screen pt-24 flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <p className="text-neutral-400">Verifying payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <Check size={40} className="text-green-500" />
            </div>
            <h1 className="text-3xl font-bold mb-4 text-white" style={{ fontFamily: 'Syne' }}>Payment Successful!</h1>
            <p className="text-neutral-400 mb-8">
              Thank you for your purchase. You'll receive an email with your files shortly.
            </p>
            <Link to="/dashboard" className="btn-primary inline-block">
              View Your Orders
            </Link>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
              <Clock size={40} className="text-yellow-500" />
            </div>
            <h1 className="text-3xl font-bold mb-4 text-white" style={{ fontFamily: 'Syne' }}>Processing...</h1>
            <p className="text-neutral-400 mb-8">
              Your payment is being processed. Check your email for confirmation.
            </p>
            <Link to="/" className="btn-secondary inline-block">
              Back to Home
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <X size={40} className="text-red-500" />
            </div>
            <h1 className="text-3xl font-bold mb-4 text-white" style={{ fontFamily: 'Syne' }}>Payment Issue</h1>
            <p className="text-neutral-400 mb-8">
              There was an issue with your payment. Please try again or contact support.
            </p>
            <Link to="/cart" className="btn-primary inline-block">
              Back to Cart
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
